import { MAX_DEPTH, MAX_LEAVES, RULE_SCHEMA, type FreshnessPolicy, type Operator, type Rule, type RuleLeaf, type RuleNode } from '@signetix/rule-engine'

/**
 * Visual condition builder model ⇄ `signetix.rule` v1 (RULE_ENGINE.md).
 *
 * The builder edits a tree of groups (ALL / ANY, optionally negated) and leaves that may be
 * incomplete while the author is still choosing; only a complete tree serialises to a rule.
 * Operators are filtered by the reference's declared type (§3/§4); the value input follows the
 * operator family. No free-text expressions anywhere.
 */

export type Family = 'number' | 'text' | 'boolean' | 'list' | 'date' | 'time' | 'freshness'
export type NeedleType = 'text' | 'number'

export interface BuilderLeaf {
  kind: 'leaf'
  id: string
  ref: string | null
  family: Family | null
  op: Operator | null
  value: unknown
  unit?: string
  case_sensitive?: boolean
  freshness?: FreshnessPolicy
  /** `list` family `contains`: the needle's type. */
  needle?: NeedleType
}

export interface BuilderGroup {
  kind: 'group'
  id: string
  mode: 'all' | 'any'
  /** NOT: the group is true when its ALL / ANY is false. */
  negate: boolean
  children: BuilderNode[]
}

export type BuilderNode = BuilderLeaf | BuilderGroup

let seq = 0
export function nodeId(): string {
  seq += 1
  return `n${seq}`
}

export function emptyLeaf(): BuilderLeaf {
  return { kind: 'leaf', id: nodeId(), ref: null, family: null, op: null, value: undefined }
}

export function emptyGroup(mode: 'all' | 'any' = 'all', children: BuilderNode[] = [emptyLeaf()]): BuilderGroup {
  return { kind: 'group', id: nodeId(), mode, negate: false, children }
}

/* -------------------------------------------------------------- operators */

export const FAMILY_OPS: Readonly<Record<Family, readonly Operator[]>> = {
  number: ['eq', 'ne', 'gt', 'lt', 'gte', 'lte', 'between'],
  text: ['eq', 'ne', 'contains', 'not_contains', 'starts_with', 'ends_with'],
  boolean: ['is_true', 'is_false'],
  list: ['contains', 'not_contains', 'empty', 'not_empty'],
  date: ['before', 'after', 'between', 'today'],
  time: ['before', 'after', 'between'],
  freshness: ['freshness_is'],
}

export const FAMILY_LABELS: Readonly<Record<Family, string>> = {
  number: 'Number',
  text: 'Text',
  boolean: 'Yes / no',
  list: 'List',
  date: 'Date',
  time: 'Time of day',
  freshness: 'Data status',
}

const OP_LABELS: Readonly<Record<Family, Partial<Record<Operator, string>>>> = {
  number: { eq: 'equals', ne: 'is not', gt: 'is more than', lt: 'is less than', gte: 'is at least', lte: 'is at most', between: 'is between' },
  text: { eq: 'is', ne: 'is not', contains: 'contains', not_contains: 'does not contain', starts_with: 'starts with', ends_with: 'ends with' },
  boolean: { is_true: 'is yes', is_false: 'is no' },
  list: { contains: 'includes', not_contains: 'does not include', empty: 'is empty', not_empty: 'is not empty' },
  date: { before: 'is before', after: 'is after', between: 'is between', today: 'is today' },
  time: { before: 'is before', after: 'is at or after', between: 'is between' },
  freshness: { freshness_is: 'is' },
}

export function opLabel(family: Family, op: Operator): string {
  return OP_LABELS[family][op] ?? op
}

/** Families a declared reference type allows (RULE_ENGINE.md §4). `any` / `json` / unknown types offer all of them. */
export function familiesForType(type: string | null | undefined, ref?: string | null): Family[] {
  let out: Family[]
  switch (type) {
    case 'number':
      out = ['number']
      break
    case 'text':
    case 'url':
    case 'color':
    case 'media':
      out = ['text']
      break
    case 'boolean':
      out = ['boolean']
      break
    case 'list':
      out = ['list']
      break
    case 'date':
      out = ['date']
      break
    case 'datetime':
      out = ['date', 'time']
      break
    case 'time':
      out = ['time']
      break
    default:
      out = ['number', 'text', 'boolean', 'list', 'date', 'time']
  }
  if (ref && ref.startsWith('data.')) out = [...out, 'freshness']
  return out
}

export type ValueKind = 'none' | 'number' | 'number_range' | 'text' | 'date' | 'date_range' | 'time' | 'time_range' | 'needle' | 'freshness'

export function valueKind(family: Family | null, op: Operator | null): ValueKind {
  if (!family || !op) return 'none'
  switch (family) {
    case 'number':
      return op === 'between' ? 'number_range' : 'number'
    case 'text':
      return 'text'
    case 'boolean':
      return 'none'
    case 'list':
      return op === 'contains' || op === 'not_contains' ? 'needle' : 'none'
    case 'date':
      return op === 'between' ? 'date_range' : op === 'today' ? 'none' : 'date'
    case 'time':
      return op === 'between' ? 'time_range' : 'time'
    case 'freshness':
      return 'freshness'
  }
}

export const FRESHNESS_LABELS: Readonly<Record<string, string>> = {
  fresh: 'up to date',
  stale: 'stale',
  expired: 'expired',
  unavailable: 'unavailable',
}

/** Sensible starting value when the operator changes (keeps a compatible current value). */
export function defaultValue(kind: ValueKind, current?: unknown): unknown {
  switch (kind) {
    case 'none':
      return undefined
    case 'number':
      return typeof current === 'number' ? current : Array.isArray(current) && typeof current[0] === 'number' ? current[0] : 0
    case 'number_range':
      return Array.isArray(current) && typeof current[0] === 'number' ? current : typeof current === 'number' ? [current, current] : [0, 10]
    case 'text':
    case 'needle':
      return typeof current === 'string' || typeof current === 'number' ? String(current) : ''
    case 'date':
      return typeof current === 'string' && DATE_RE.test(current) ? current : Array.isArray(current) && typeof current[0] === 'string' && DATE_RE.test(current[0]) ? current[0] : ''
    case 'date_range':
      return Array.isArray(current) && typeof current[0] === 'string' && DATE_RE.test(current[0]) ? current : ['', '']
    case 'time':
      return typeof current === 'string' && TIME_RE.test(current) ? current : Array.isArray(current) && typeof current[0] === 'string' && TIME_RE.test(current[0]) ? current[0] : '09:00'
    case 'time_range':
      return Array.isArray(current) && typeof current[0] === 'string' && TIME_RE.test(current[0]) ? current : ['09:00', '17:00']
    case 'freshness':
      return typeof current === 'string' && current in FRESHNESS_LABELS ? current : 'expired'
  }
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/
const TIME_END_RE = /^(([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?|24:00)$/

/* -------------------------------------------------------------- leaf edits */

/** Picks a reference: chooses the first allowed family and a fitting operator. */
export function withRef(leaf: BuilderLeaf, ref: string, type: string | null | undefined, preferred?: Family | null): BuilderLeaf {
  const families = familiesForType(type, ref)
  const family = preferred && families.includes(preferred) ? preferred : leaf.family && families.includes(leaf.family) && leaf.ref === ref ? leaf.family : families[0]!
  return withFamily({ ...leaf, ref, ...(ref.startsWith('data.') ? {} : { freshness: undefined }) }, family)
}

export function withFamily(leaf: BuilderLeaf, family: Family): BuilderLeaf {
  const ops = FAMILY_OPS[family]
  const op = leaf.op && ops.includes(leaf.op) && leaf.family === family ? leaf.op : ops[0]!
  return withOp({ ...leaf, family, unit: family === 'number' ? leaf.unit : undefined, case_sensitive: family === 'text' || family === 'list' ? leaf.case_sensitive : undefined }, op)
}

export function withOp(leaf: BuilderLeaf, op: Operator): BuilderLeaf {
  const kind = valueKind(leaf.family, op)
  return { ...leaf, op, value: defaultValue(kind, leaf.value), ...(kind === 'needle' ? { needle: leaf.needle ?? 'text' } : {}) }
}

/** Missing-part message of a leaf, or null when complete. */
export function leafProblem(leaf: BuilderLeaf): string | null {
  if (!leaf.ref) return 'Choose what to check'
  if (!leaf.family || !leaf.op) return 'Choose a comparison'
  const v = leaf.value
  switch (valueKind(leaf.family, leaf.op)) {
    case 'none':
      return null
    case 'number':
      return typeof v === 'number' && Number.isFinite(v) ? null : 'Enter a number'
    case 'number_range':
      if (!Array.isArray(v) || !v.every((x) => typeof x === 'number' && Number.isFinite(x))) return 'Enter both numbers'
      return (v[0] as number) <= (v[1] as number) ? null : 'The first number must not be larger than the second'
    case 'text':
      return typeof v === 'string' && v !== '' ? null : 'Enter a value'
    case 'needle':
      if (leaf.needle === 'number') return typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? null : 'Enter a number'
      return typeof v === 'string' && v !== '' ? null : 'Enter a value'
    case 'date':
      return typeof v === 'string' && DATE_RE.test(v) ? null : 'Pick a date'
    case 'date_range':
      if (!Array.isArray(v) || !v.every((x) => typeof x === 'string' && DATE_RE.test(x))) return 'Pick both dates'
      return (v[0] as string) <= (v[1] as string) ? null : 'The first date must not be after the second'
    case 'time':
      return typeof v === 'string' && TIME_RE.test(v) ? null : 'Pick a time'
    case 'time_range':
      return Array.isArray(v) && typeof v[0] === 'string' && TIME_RE.test(v[0]) && typeof v[1] === 'string' && TIME_END_RE.test(v[1]) ? null : 'Pick both times'
    case 'freshness':
      return typeof v === 'string' && v in FRESHNESS_LABELS ? null : 'Choose a status'
  }
}

/* -------------------------------------------------------------- serialise */

export function leafToRule(leaf: BuilderLeaf): RuleLeaf | null {
  if (leafProblem(leaf)) return null
  const out: RuleLeaf = { ref: leaf.ref!, op: leaf.op! }
  const kind = valueKind(leaf.family, leaf.op)
  if (kind === 'needle') out.value = leaf.needle === 'number' ? Number(leaf.value) : (leaf.value as string)
  else if (kind !== 'none') out.value = leaf.value as RuleLeaf['value']
  if (leaf.family === 'number' && leaf.unit) out.unit = leaf.unit
  const textual = leaf.family === 'text' || (kind === 'needle' && leaf.needle !== 'number')
  if (textual && leaf.case_sensitive) out.case_sensitive = true
  if (leaf.freshness === 'allow_stale' && leaf.ref!.startsWith('data.') && leaf.op !== 'freshness_is') out.freshness = 'allow_stale'
  return out
}

export interface SerializeResult {
  /** The rule when every part is complete. */
  rule: Rule | null
  /** Leaf id → problem (incomplete leaves, empty groups use the group id). */
  problems: Record<string, string>
  /** JSON pointer of each leaf in the produced rule → builder node id. */
  pointers: Record<string, string>
  leaves: number
  depth: number
}

function groupToNode(g: BuilderGroup, base: string, ctx: { problems: Record<string, string>; pointers: Record<string, string>; ok: boolean }): RuleNode | null {
  if (!g.children.length) {
    ctx.problems[g.id] = 'Add a condition to this group'
    ctx.ok = false
  }
  const single = g.negate && g.children.length === 1
  const listBase = g.negate ? (single ? `${base}/not` : `${base}/not/${g.mode}`) : `${base}/${g.mode}`
  const children = g.children.map((c, i) => {
    const at = single ? listBase : `${listBase}/${i}`
    if (c.kind === 'group') return groupToNode(c, at, ctx)
    const leaf = leafToRule(c)
    if (!leaf) {
      ctx.problems[c.id] = leafProblem(c) ?? 'Incomplete'
      ctx.ok = false
      return null
    }
    ctx.pointers[at] = c.id
    return leaf
  })
  if (!ctx.ok) return null
  const list = children as RuleNode[]
  if (g.negate) return single ? { not: list[0]! } : { not: g.mode === 'all' ? { all: list } : { any: list } }
  return g.mode === 'all' ? { all: list } : { any: list }
}

/** Builder tree → rule (null while incomplete), plus per-node problems and leaf pointers. */
export function toRule(root: BuilderGroup): SerializeResult {
  const ctx = { problems: {} as Record<string, string>, pointers: {} as Record<string, string>, ok: true }
  const node = groupToNode(root, '', ctx)
  const leaves = countLeaves(root)
  const depth = treeDepth(root)
  if (leaves > MAX_LEAVES) {
    ctx.problems[root.id] = `At most ${MAX_LEAVES} conditions`
    ctx.ok = false
  }
  if (depth > MAX_DEPTH) {
    ctx.problems[root.id] = `Groups can be nested at most ${MAX_DEPTH} levels deep`
    ctx.ok = false
  }
  const rule = ctx.ok && node ? ({ schema: RULE_SCHEMA, version: 1, ...node } as Rule) : null
  return { rule, problems: ctx.problems, pointers: ctx.pointers, leaves, depth }
}

export function countLeaves(n: BuilderNode): number {
  return n.kind === 'leaf' ? 1 : n.children.reduce((s, c) => s + countLeaves(c), 0)
}

/** Serialised nesting depth (root = 1; `not` counts as a level, a negated single child saves one). */
export function treeDepth(n: BuilderNode): number {
  if (n.kind === 'leaf') return 0
  const inner = n.children.reduce((m, c) => Math.max(m, treeDepth(c)), 0)
  if (!n.negate) return 1 + inner
  return n.children.length === 1 ? 1 + inner : 2 + inner
}

/* ------------------------------------------------------------------ parse */

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

/** Family of a stored leaf from its operator and value shape (and the declared type for `contains`). */
export function inferFamily(op: Operator, value: unknown, type?: string | null): Family {
  switch (op) {
    case 'eq':
    case 'ne':
      return typeof value === 'number' ? 'number' : 'text'
    case 'gt':
    case 'lt':
    case 'gte':
    case 'lte':
      return 'number'
    case 'between':
      if (Array.isArray(value) && typeof value[0] === 'number') return 'number'
      return Array.isArray(value) && typeof value[0] === 'string' && DATE_RE.test(value[0]) ? 'date' : 'time'
    case 'contains':
    case 'not_contains':
      return typeof value !== 'string' || type === 'list' ? 'list' : 'text'
    case 'starts_with':
    case 'ends_with':
      return 'text'
    case 'is_true':
    case 'is_false':
      return 'boolean'
    case 'empty':
    case 'not_empty':
      return 'list'
    case 'before':
    case 'after':
      return typeof value === 'string' && DATE_RE.test(value) ? 'date' : 'time'
    case 'today':
      return 'date'
    case 'freshness_is':
      return 'freshness'
  }
}

function parseNode(n: unknown, typeOf: (ref: string) => string | null | undefined): BuilderNode {
  if (isObj(n) && Array.isArray(n.all)) return { kind: 'group', id: nodeId(), mode: 'all', negate: false, children: n.all.map((c) => parseNode(c, typeOf)) }
  if (isObj(n) && Array.isArray(n.any)) return { kind: 'group', id: nodeId(), mode: 'any', negate: false, children: n.any.map((c) => parseNode(c, typeOf)) }
  if (isObj(n) && 'not' in n) {
    const inner = parseNode(n.not, typeOf)
    if (inner.kind === 'group' && !inner.negate) return { ...inner, negate: true }
    return { kind: 'group', id: nodeId(), mode: 'all', negate: true, children: [inner] }
  }
  const leaf = isObj(n) ? n : {}
  const ref = typeof leaf.ref === 'string' ? leaf.ref : null
  const op = typeof leaf.op === 'string' ? (leaf.op as Operator) : null
  const family = op && ref ? inferFamily(op, leaf.value, typeOf(ref)) : null
  const kind = valueKind(family, op)
  const value = kind === 'needle' ? (leaf.value === undefined ? '' : String(leaf.value)) : leaf.value
  return {
    kind: 'leaf',
    id: nodeId(),
    ref,
    family,
    op,
    value,
    ...(typeof leaf.unit === 'string' ? { unit: leaf.unit } : {}),
    ...(leaf.case_sensitive === true ? { case_sensitive: true } : {}),
    ...(leaf.freshness === 'allow_stale' || leaf.freshness === 'require_fresh' ? { freshness: leaf.freshness as FreshnessPolicy } : {}),
    ...(kind === 'needle' ? { needle: typeof leaf.value === 'number' ? 'number' : 'text' } : {}),
  }
}

/** Rule → builder tree (the root is always a group). A missing / unreadable rule gives one empty condition. */
export function fromRule(rule: unknown, typeOf: (ref: string) => string | null | undefined = () => null): BuilderGroup {
  if (!isObj(rule)) return emptyGroup()
  const { schema: _s, version: _v, ...body } = rule
  void _s
  void _v
  const root = parseNode(body, typeOf)
  return root.kind === 'group' ? root : emptyGroup('all', [root])
}

/* ------------------------------------------------------------- tree edits */

export function mapNode(root: BuilderGroup, id: string, fn: (n: BuilderNode) => BuilderNode | null): BuilderGroup {
  const walk = (n: BuilderNode): BuilderNode | null => {
    if (n.id === id) return fn(n)
    if (n.kind === 'leaf') return n
    let changed = false
    const children: BuilderNode[] = []
    for (const c of n.children) {
      const next = walk(c)
      if (next !== c) changed = true
      if (next) children.push(next)
    }
    return changed ? { ...n, children } : n
  }
  const out = walk(root)
  return out && out.kind === 'group' ? out : root
}

export function findNode(root: BuilderNode, id: string): BuilderNode | null {
  if (root.id === id) return root
  if (root.kind === 'leaf') return null
  for (const c of root.children) {
    const hit = findNode(c, id)
    if (hit) return hit
  }
  return null
}

/** Nesting depth of a group inside the tree (root = 1), counting negations like the engine. */
export function groupLevel(root: BuilderGroup, id: string): number {
  const walk = (g: BuilderGroup, level: number): number => {
    const own = level + (g.negate && g.children.length > 1 ? 1 : 0)
    if (g.id === id) return own
    for (const c of g.children) {
      if (c.kind === 'group') {
        const hit = walk(c, own + 1)
        if (hit) return hit
      }
    }
    return 0
  }
  return walk(root, 1)
}

/** Whether a sub-group can be added inside `groupId` without exceeding the engine depth. */
export function canAddGroup(root: BuilderGroup, groupId: string): boolean {
  const next = mapNode(root, groupId, (n) => (n.kind === 'group' ? { ...n, children: [...n.children, emptyGroup('any')] } : n))
  return treeDepth(next) <= MAX_DEPTH
}

export function canNegate(root: BuilderGroup, groupId: string): boolean {
  const next = mapNode(root, groupId, (n) => (n.kind === 'group' ? { ...n, negate: true } : n))
  return treeDepth(next) <= MAX_DEPTH
}

/* --------------------------------------------------------------- summary */

export type RefLabel = (ref: string) => string

function fmtValue(leaf: BuilderLeaf): string {
  const v = leaf.value
  const unit = leaf.unit ? ` ${UNIT_WORDS[leaf.unit] ?? leaf.unit}` : ''
  switch (valueKind(leaf.family, leaf.op)) {
    case 'none':
      return ''
    case 'number':
      return `${String(v)}${unit}`
    case 'number_range':
    case 'date_range':
    case 'time_range':
      return Array.isArray(v) ? `${String(v[0])} and ${String(v[1])}${unit}` : '…'
    case 'text':
      return `“${String(v ?? '')}”`
    case 'needle':
      return leaf.needle === 'number' ? String(v) : `“${String(v ?? '')}”`
    case 'date':
    case 'time':
      return String(v ?? '…')
    case 'freshness':
      return FRESHNESS_LABELS[String(v)] ?? String(v)
  }
}

const UNIT_WORDS: Record<string, string> = { celsius: '°C', fahrenheit: '°F', percent: '%', kmh: 'km/h', mph: 'mph', ms: 'm/s', mm: 'mm', cm: 'cm', m: 'm', km: 'km', g: 'g', kg: 'kg', lb: 'lb', l: 'l', ml: 'ml', kwh: 'kWh', w: 'W' }

export function leafSummary(leaf: BuilderLeaf, label: RefLabel): string {
  if (!leaf.ref || !leaf.family || !leaf.op) return '…'
  const subject = leaf.family === 'freshness' ? `${label(leaf.ref)} data` : label(leaf.ref)
  const value = fmtValue(leaf)
  return [subject, opLabel(leaf.family, leaf.op), value].filter(Boolean).join(' ')
}

/** Human sentence for a builder tree: “Country is “RS” and (temperature is more than 30 °C or humidity is more than 80)”. */
export function summarize(n: BuilderNode, label: RefLabel, nested = false): string {
  if (n.kind === 'leaf') return leafSummary(n, label)
  const parts = n.children.map((c) => summarize(c, label, true))
  const joined = parts.join(n.mode === 'all' ? ' and ' : ' or ')
  if (n.negate) return parts.length === 1 ? `not (${parts[0]})` : `not (${joined})`
  return nested && parts.length > 1 ? `(${joined})` : joined || '…'
}

/** Summary of a stored rule document. */
export function ruleSummary(rule: unknown, label: RefLabel, typeOf?: (ref: string) => string | null | undefined): string {
  return summarize(fromRule(rule, typeOf), label)
}

/* ------------------------------------------------------------------ misc */

/** Stable JSON (sorted object keys) — rule identity for reuse and caching. */
export function canonicalJson(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonicalJson).join(',')}]`
  if (isObj(v))
    return `{${Object.keys(v)
      .filter((k) => v[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonicalJson(v[k])}`)
      .join(',')}}`
  return JSON.stringify(v) ?? 'null'
}

/** Every leaf ref of a (possibly invalid) rule, document order, unique. */
export function ruleRefs(rule: unknown): string[] {
  const out: string[] = []
  const walk = (n: unknown, depth: number) => {
    if (!isObj(n) || depth > 16) return
    if (typeof n.ref === 'string' && !out.includes(n.ref)) out.push(n.ref)
    for (const k of ['all', 'any'] as const) if (Array.isArray(n[k])) for (const c of n[k] as unknown[]) walk(c, depth + 1)
    if ('not' in n) walk(n.not, depth + 1)
  }
  walk(rule, 0)
  return out
}
